import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface NetworkStatus {
  isOnline: boolean;
  connectionType?: string;
  effectiveType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NetworkStatusService {
  private _networkStatus = new BehaviorSubject<NetworkStatus>({ isOnline: navigator.onLine });

  public networkStatus$ = this._networkStatus.asObservable();
  public isOnline$ = this.networkStatus$.pipe(map((status) => status.isOnline));

  constructor() {
    this.initializeNetworkMonitoring();
  }

  private initializeNetworkMonitoring(): void {
    // Monitor online/offline events
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    // Combine online/offline events with initial state
    merge(of(navigator.onLine), online$, offline$).subscribe((isOnline) => {
      const status: NetworkStatus = {
        isOnline,
        connectionType: this.getConnectionType(),
        effectiveType: this.getEffectiveType(),
      };
      this._networkStatus.next(status);
    });

    // Monitor connection changes if supported
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        connection.addEventListener('change', () => {
          const currentStatus = this._networkStatus.value;
          this._networkStatus.next({
            ...currentStatus,
            connectionType: this.getConnectionType(),
            effectiveType: this.getEffectiveType(),
          });
        });
      }
    }
  }

  private getConnectionType(): string | undefined {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return connection?.type || 'unknown';
    }
    return undefined;
  }

  private getEffectiveType(): string | undefined {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return connection?.effectiveType || 'unknown';
    }
    return undefined;
  }

  public getCurrentStatus(): NetworkStatus {
    return this._networkStatus.value;
  }

  public isCurrentlyOnline(): boolean {
    return this._networkStatus.value.isOnline;
  }
}
